import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Whether a key was raised inside a control whose own popup is open — a dropdown
 * with its list showing, a calendar with its overlay up. Such a control owns that
 * Escape: it closes the popup, the surface stays. Read from the DOM rather than
 * from `defaultPrevented` (PrimeReact's overlays call `preventDefault()` on every
 * Escape whether or not a panel is open, so a closed dropdown would swallow the
 * surface's Escape for as long as it held focus). Two markers, because a popup is
 * not one element: `aria-expanded="true"` on the control that opened it, and
 * `data-popup-open="true"` on the popup itself — `DropdownAtom` sets the latter on
 * its root AND its option panel, since the panel is portalled away from the control
 * and its filter input raises Escapes of its own.
 */
const OPEN_POPUP_SELECTOR = '[aria-expanded="true"], [data-popup-open="true"]';
function isInsideOpenPopupControl(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(OPEN_POPUP_SELECTOR) !== null;
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const nodes = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  return Array.from(nodes).filter(
    (el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null,
  );
}

export interface DialogFocusOptions {
  /**
   * Dismisses the surface when Escape is pressed. Omit only when the surface
   * genuinely cannot be dismissed from the keyboard.
   */
  onDismiss?: () => void;
  /**
   * Confines Tab to the container. True for a modal — anything that renders
   * `aria-modal="true"` owes this. False for a non-modal panel that opens in
   * place and leaves the rest of the page reachable; there, Escape is scoped to
   * keys raised inside the panel so it cannot swallow the page's own Escape.
   */
  trapFocus?: boolean;
  /** Moves focus into the container on open and returns it to the opener on close. */
  manageFocus?: boolean;
}

/**
 * Gives a dialog the behaviour its role promises:
 * - On `active` transition to true: stores current document.activeElement,
 *   then moves focus to the first focusable element inside `containerRef`.
 * - While `active` is true: traps Tab and Shift+Tab within the container.
 * - While `active` is true: Escape calls `onDismiss` — unless it was raised inside
 *   a control whose own popup was open (`aria-expanded="true"` on the control or
 *   `data-popup-open="true"` on the popup, at the moment of the key), which keeps
 *   that Escape for closing the popup.
 * - On `active` transition to false: restores focus to the previously active
 *   element.
 *
 * Pass a ref pointing at the dialog's inner content wrapper (not the overlay).
 */
export function useDialogFocus(
  active: boolean,
  containerRef: React.RefObject<HTMLElement>,
  options: DialogFocusOptions = {},
): void {
  const { onDismiss, trapFocus = true, manageFocus = true } = options;
  const previousActiveRef = useRef<HTMLElement | null>(null);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!active || !manageFocus) return undefined;

    previousActiveRef.current = document.activeElement as HTMLElement | null;

    const container = containerRef.current;
    if (container) {
      const focusables = getFocusableElements(container);
      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        if (!container.hasAttribute('tabindex')) {
          container.setAttribute('tabindex', '-1');
        }
        container.focus();
      }
    }

    return () => {
      const previous = previousActiveRef.current;
      if (previous && typeof previous.focus === 'function') {
        previous.focus();
      }
    };
  }, [active, containerRef, manageFocus]);

  useEffect(() => {
    if (!active || !trapFocus) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusables = getFocusableElements(container);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const current = document.activeElement as HTMLElement | null;

      if (!current || !container.contains(current)) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (e.shiftKey) {
        if (current === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (current === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, containerRef, trapFocus]);

  useEffect(() => {
    if (!active || !onDismissRef.current) return undefined;

    // A modal owns the page, so it listens at the document; a non-modal panel
    // listens at its own container so Escape elsewhere is not its business.
    const target: EventTarget | null = trapFocus ? document : containerRef.current;
    if (!target) return undefined;

    // Popup ownership is read in the CAPTURE phase, before any handler runs. By
    // the bubble phase React has already closed the panel and re-rendered
    // `aria-expanded="false"`, so the DOM would say the popup was never open —
    // and the surface would close on the very Escape that closed the list.
    const ownedByOpenPopup = new WeakSet<Event>();
    const snapshotOwnership = (event: Event) => {
      if ((event as KeyboardEvent).key === 'Escape' && isInsideOpenPopupControl(event.target)) {
        ownedByOpenPopup.add(event);
      }
    };
    const handleKeyDown = (event: Event) => {
      const e = event as KeyboardEvent;
      if (e.key !== 'Escape' || ownedByOpenPopup.has(e)) return;
      e.stopPropagation();
      onDismissRef.current?.();
    };

    target.addEventListener('keydown', snapshotOwnership, true);
    target.addEventListener('keydown', handleKeyDown);
    return () => {
      target.removeEventListener('keydown', snapshotOwnership, true);
      target.removeEventListener('keydown', handleKeyDown);
    };
  }, [active, containerRef, trapFocus]);
}
